import { createContext, useContext, useState, useEffect } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);

  // Load permissions when user changes
  const loadPermissions = async (userRole) => {
    if (!userRole) {
      setPermissions(null);
      return;
    }
    try {
      const res = await api.get(`/role-permissions/${userRole}`);
      setPermissions(res.data.permissions);
    } catch (error) {
      console.error("Error loading permissions:", error);
      // Default fallback
      setPermissions({
        tableau_de_bord: userRole === "admin",
        stock: true,
        backmarket: true,
        lots_achat: true,
        preparations: true,
        atelier: true,
        validation_reparations: ["admin", "manager"].includes(userRole),
        lots_vente: ["admin", "manager"].includes(userRole),
        fournisseurs: userRole !== "technicien",
        clients: userRole !== "technicien",
        commandes: userRole !== "technicien",
        utilisateurs: userRole === "admin",
        parametres: userRole !== "technicien",
        donnees_financieres: userRole !== "technicien"
      });
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      api.get("/auth/me")
        .then((res) => {
          setUser(res.data);
          loadPermissions(res.data.role);
        })
        .catch(() => {
          localStorage.removeItem("token");
          delete api.defaults.headers.common["Authorization"];
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const { token, user } = res.data;
    localStorage.setItem("token", token);
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(user);
    await loadPermissions(user.role);
    return user;
  };

  const register = async (email, password, nom) => {
    const res = await api.post("/auth/register", { email, password, nom });
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete api.defaults.headers.common["Authorization"];
    setUser(null);
    setPermissions(null);
  };

  // Function to refresh permissions (useful after admin changes them)
  const refreshPermissions = async () => {
    if (user?.role) {
      await loadPermissions(user.role);
    }
  };

  // Helper to check if user has permission for financial data
  const canViewFinancialData = () => {
    return permissions?.donnees_financieres ?? (user?.role !== "technicien");
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      permissions, 
      refreshPermissions,
      canViewFinancialData 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
